import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { Phone, Star, ArrowUpRight, Wifi, Car, Wind, Coffee, Waves, ShieldCheck, Sparkles, Handshake, ChevronLeft, ChevronRight } from "lucide-react";
import { endpoints, asset, api } from "../lib/api";
import { openBookingModal } from "../lib/bookingModal";
import { Reveal, Parallax } from "../components/Reveal";
import BestRateBadge from "../components/BestRateBadge";

const HERO = asset("/static/generated/byer-front-pool.png");
const HEADLINE = ["Comfort", "Beyond", "Expectations"];

const CHAPTERS = [
  ["01", "Genuine Country Hospitality", "Family-run warmth in the heart of Holbrook — where every guest is welcomed like an old friend, not a room number."],
  ["02", "Thirty-Six Ground-Floor Rooms", "Park directly outside your door. Queen, Twin, Family and interconnecting layouts — all on one easy, accessible level."],
  ["03", "The Halfway Haven", "The perfect rest between Sydney and Melbourne on the Hume Highway, and home to authentic Indian dining."],
];

const PERKS = [
  [Car, "Parking at your door"],
  [Wifi, "Free Wi-Fi"],
  [Wind, "Air conditioning"],
  [Coffee, "Tea & Coffee"],
];

export default function Home() {
  const [rooms, setRooms] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [offers, setOffers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [awards, setAwards] = useState([]);
  const [covidText, setCovidText] = useState("");

  useEffect(() => {
    endpoints.rooms().then(setRooms).catch(() => {});
    endpoints.offers().then(setOffers).catch(() => {});
    api.get("/facilities").then((r) => setFacilities(r.data)).catch(() => {});
    api.get("/awards").then((r) => setAwards(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => setCovidText(r.data.covid_safe_text || "")).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[100svh] min-h-[640px] flex flex-col justify-center overflow-hidden bg-[#0B192C]" data-testid="hero">
        <Parallax className="absolute inset-0" distance={80}>
          <img src={HERO} alt="Byer Fountain Motor Inn at golden hour" className="w-full h-full object-cover" />
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B192C]/80 via-[#0B192C]/45 to-[#0B192C]/85" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full pt-24 sm:pt-16 md:pt-8 lg:pt-0">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
            className="overline text-[#D4AF37] mb-5">Holbrook · New South Wales · Australia</motion.p>

          <h1 className="font-serif text-white text-[3.4rem] leading-[0.92] sm:text-7xl lg:text-[7.5rem] tracking-tight">
            {HEADLINE.map((w, i) => (
              <span key={w} className="mask-line">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] }}>
                  {w === "Beyond" ? <em className="text-[#D4AF37] not-italic font-semibold">{w}</em> : w}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }}
            className="mt-7 max-w-xl text-lg text-white/80 font-light">
            Experience genuine country hospitality in the heart of Holbrook.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1 }}
            className="mt-9 flex flex-wrap gap-3">
            <button onClick={() => openBookingModal()} data-testid="hero-book"
              className="bg-[#D4AF37] text-[#0B192C] px-7 py-3.5 rounded-full font-medium hover:bg-white transition-colors">Book Now</button>
            <Link to="/corporate" data-testid="hero-corporate"
              className="glass-dark text-white px-7 py-3.5 rounded-full font-medium hover:border-[#D4AF37] transition-colors">Corporate Accommodation</Link>
            <Link to="/rooms" data-testid="hero-rooms"
              className="text-white px-7 py-3.5 rounded-full font-medium border border-white/30 hover:bg-white/10 transition-colors">Explore Rooms</Link>
            <a href="tel:0260362077" data-testid="hero-call"
              className="text-white px-6 py-3.5 rounded-full font-medium border border-white/30 hover:bg-white/10 transition-colors flex items-center gap-2"><Phone size={16}/> Call Now</a>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9, duration: 1 }}
          className="relative z-20 max-w-7xl mx-auto px-5 md:px-8 w-full mt-14 flex items-center gap-3 text-white/50 text-xs tracking-[0.2em] uppercase">
          <span className="w-10 h-px bg-[#D4AF37]" /> Scroll to explore
        </motion.div>
      </section>

      {/* BOOK-DIRECT LIVE RATE BADGE */}
      <BestRateBadge />

      {/* MANIFESTO */}
      <section className="bg-[#F7F5F0] pt-28 pb-28" data-testid="manifesto">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <Reveal><p className="overline text-[#D4AF37] mb-4">Our Promise</p></Reveal>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-16 mt-8">
            {CHAPTERS.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.12}>
                <div className="relative">
                  <span className="absolute -top-14 -left-2 font-serif text-8xl text-[#0B192C]/[0.06] select-none">{n}</span>
                  <h3 className="relative font-serif text-3xl text-[#0B192C] leading-tight mb-3">{t}</h3>
                  <p className="text-[#222]/70 leading-relaxed font-light">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#0B192C] py-10 border-y border-[#D4AF37]/20" data-testid="marquee">
        <Marquee speed={40} gradient={false} autoFill>
          {["Rest", "Dine", "Recharge", "Explore", "Belong", "Unwind"].map((w) => (
            <span key={w} className="font-serif text-5xl md:text-6xl text-white/90 mx-10 flex items-center">
              {w} <Star size={16} className="text-[#D4AF37] ml-10" fill="#D4AF37" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* ROOMS */}
      <section className="bg-[#F7F5F0] py-28" data-testid="rooms-preview">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <p className="overline text-[#D4AF37] mb-3">Accommodation</p>
              <h2 className="font-serif text-5xl md:text-6xl text-[#0B192C] tracking-tight">Rooms for every journey</h2>
            </div>
            <Link to="/rooms" className="group flex items-center gap-2 text-[#0B192C] hover:text-[#D4AF37] transition-colors" data-testid="rooms-viewall">
              View all rooms <ArrowUpRight className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={20} />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {rooms.slice(0, 6).map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.08}>
                <div className="group rounded-2xl overflow-hidden bg-white soft-shadow border border-[#0B192C]/5" data-testid={`room-card-${r.slug}`}>
                  <div className="relative h-60 overflow-hidden">
                    <img src={asset(r.image)} alt={r.name} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                    <span className="absolute top-4 left-4 glass-dark text-white text-xs px-3 py-1 rounded-full">{r.capacity}</span>
                  </div>
                  <div className="p-6">
                    <p className="overline text-[#D4AF37] mb-1 text-[10px]">{r.tagline}</p>
                    <h3 className="font-serif text-2xl text-[#0B192C]">{r.name}</h3>
                    <p className="text-sm text-[#222]/60 mt-2 leading-relaxed line-clamp-2">{r.description}</p>
                    <div className="flex items-center justify-between mt-5">
                      <span className="text-xs text-[#222]/50">{r.beds}</span>
                      <button onClick={() => openBookingModal()} className="text-sm font-medium text-[#0B192C] border border-[#0B192C]/20 rounded-full px-4 py-2 hover:bg-[#0B192C] hover:text-white transition-colors" data-testid={`room-book-${r.slug}`}>Book</button>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 flex flex-wrap gap-8 justify-center">
            {PERKS.map(([Icon, label]) => (
              <div key={label} className="flex items-center gap-2.5 text-[#0B192C]/70">
                <Icon size={20} className="text-[#D4AF37]" /> <span className="text-sm">{label}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* DINING TEASER */}
      <section className="relative bg-[#0B192C] text-white overflow-hidden" data-testid="dining-teaser">
        <div className="grid md:grid-cols-2">
          <Parallax className="h-72 md:h-auto min-h-[420px]" distance={50}>
            <img src={asset("/static/generated/dish-lamb-shanks-in-red-wine-rosemary-sauce.png")} alt="Restaurant dish" className="w-full h-full object-cover" />
          </Parallax>
          <div className="p-10 md:p-20 flex flex-col justify-center grain">
            <Reveal>
              <p className="overline text-[#D4AF37] mb-4">On-Site Restaurant</p>
              <h2 className="font-serif text-5xl md:text-6xl leading-tight">Hearty Classics &amp; Indian Favourites</h2>
              <p className="mt-5 text-white/70 font-light max-w-md leading-relaxed">
                From pub-style parmi, schnitzel and lamb shanks to slow-cooked Dum Biryani and velvety Butter Chicken — a full à-la-carte menu to end your day. Pre-order and it's ready when you arrive.
              </p>
              <Link to="/dining" data-testid="dining-explore"
                className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B192C] px-7 py-3.5 rounded-full font-medium hover:bg-white transition-colors w-fit">
                Explore the Menu <ArrowUpRight size={18} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OFFERS */}
      <section className="bg-[#F7F5F0] py-28" data-testid="offers">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal><h2 className="font-serif text-5xl md:text-6xl text-[#0B192C] text-center mb-16">Why book direct</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-7">
            {offers.map((o, i) => (
              <Reveal key={o.id} delay={i * 0.1}>
                <div className="rounded-2xl border border-[#D4AF37]/30 p-8 h-full bg-white soft-shadow">
                  <span className="overline text-[#D4AF37]">{o.badge}</span>
                  <h3 className="font-serif text-3xl text-[#0B192C] mt-3 mb-3">{o.title}</h3>
                  <p className="text-[#222]/70 font-light leading-relaxed">{o.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FACILITIES */}
      <section className="bg-[#F7F5F0] py-28" data-testid="facilities">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal><p className="overline text-[#D4AF37] mb-3">Facilities & Amenities</p>
            <h2 className="font-serif text-5xl md:text-6xl text-[#0B192C] tracking-tight mb-14">Everything for a restful stay</h2></Reveal>
          <div className="grid lg:grid-cols-3 gap-7">
            {facilities.find((f) => f.image) && (
              <Reveal className="lg:row-span-2 lg:col-span-1">
                <div className="rounded-2xl overflow-hidden soft-shadow h-full min-h-[320px] relative group" data-testid="facility-pool">
                  <img src={asset(facilities.find((f) => f.image).image)} alt={facilities.find((f) => f.image).name}
                    className="w-full h-full object-cover absolute inset-0 transition-transform duration-[1.4s] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/85 to-transparent" />
                  <div className="absolute bottom-0 p-7">
                    <Waves className="text-[#D4AF37] mb-2" size={26} />
                    <h3 className="font-serif text-3xl text-white">{facilities.find((f) => f.image).name}</h3>
                    <p className="text-white/75 text-sm mt-1 font-light">{facilities.find((f) => f.image).description}</p>
                  </div>
                </div>
              </Reveal>
            )}
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
              {facilities.filter((f) => !f.image).map((f, i) => (
                <Reveal key={f.id} delay={(i % 2) * 0.08}>
                  <div className="rounded-2xl bg-white soft-shadow border border-[#0B192C]/5 p-6 h-full" data-testid={`facility-${f.id}`}>
                    <h3 className="font-serif text-2xl text-[#0B192C]">{f.name}</h3>
                    <p className="text-[#222]/65 text-sm mt-2 font-light leading-relaxed">{f.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AWARDS & RECOGNITION — two-column responsible layout */}
      {(awards.length > 0 || covidText) && (
        <section className="bg-[#0B192C] py-24 md:py-28 grain relative overflow-hidden" data-testid="awards">
          <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
            <Reveal className="max-w-2xl mb-12">
              <p className="overline text-[#D4AF37] mb-3">Awards &amp; Recognition</p>
              <h2 className="font-serif text-4xl md:text-6xl text-white">Trusted for genuine, quality hospitality</h2>
            </Reveal>

            <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
              {/* Left: professional imagery */}
              {awards[0] && (
                <Reveal className="lg:col-span-2">
                  <div className="rounded-2xl overflow-hidden bg-white/5 border border-[#D4AF37]/25 p-4 md:p-5" data-testid="awards-image">
                    <img src={asset(awards[0].image)} alt={awards[0].title || "Byer Fountain Motor Inn — Awards & Recognition"} className="w-full h-auto rounded-xl" loading="lazy" />
                  </div>
                </Reveal>
              )}

              {/* Right: three pillars with responsible verbiage */}
              <Reveal delay={0.08} className="lg:col-span-3 space-y-6">
                {[
                  [Handshake, "Guest Satisfaction",
                    "Consistently praised for our warm, family-style welcome, spotless ground-floor rooms and genuine attention to detail — as reflected in the many independent reviews left by our guests."],
                  [Sparkles, "Quality Accommodation",
                    "We take great pride in maintaining every one of our thirty-six rooms to a high standard, with comfortable bedding, well-kept furnishings and a peaceful setting for a restful stay."],
                  [ShieldCheck, "COVID-Safe Practices",
                    covidText || "We continue to maintain enhanced hygiene and cleaning practices in line with the NSW COVID-Safe framework — including thorough room turnovers, contactless check-in options and hand-sanitiser stations for the comfort of every guest."],
                ].map(([Icon, title, body]) => (
                  <div key={title} className="bg-white/5 border border-white/10 rounded-2xl p-5 md:p-6 backdrop-blur-sm" data-testid={`recognition-${title.split(" ").join("-").toLowerCase()}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 shrink-0 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 grid place-items-center">
                        <Icon size={18} className="text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="font-serif text-2xl text-white leading-tight">{title}</p>
                        <p className="text-white/70 text-sm md:text-[15px] mt-2 leading-relaxed font-light">{body}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* GUEST REVIEWS (real, curated from Google — hidden until at least one review is added by staff) */}
      {reviews.length > 0 && (
        <section className="bg-[#0B192C] py-24 md:py-28 grain relative overflow-hidden" data-testid="reviews">
          <div className="max-w-5xl mx-auto px-5 md:px-8 relative z-10">
            <Reveal className="text-center mb-10">
              <p className="overline text-[#D4AF37] mb-3">Guest Reviews · from Google</p>
              <h2 className="font-serif text-4xl md:text-6xl text-white">Loved by our guests</h2>
              <p className="text-white/55 mt-4 text-sm max-w-xl mx-auto font-light">
                Genuine reviews from travellers who've stayed with us. Read every review on {" "}
                <a className="text-[#D4AF37] underline underline-offset-4" href="https://www.google.com/maps/search/?api=1&query=Byer+Fountain+Motor+Inn+Holbrook" target="_blank" rel="noreferrer">Google Maps</a>.
              </p>
            </Reveal>

            <ReviewsCarousel reviews={reviews} idx={reviewIdx} setIdx={setReviewIdx} />
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-[#F7F5F0] py-28" data-testid="final-cta">
        <Reveal className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="font-serif text-5xl md:text-7xl text-[#0B192C] leading-tight">Your room in Holbrook<br/>is waiting.</h2>
          <p className="text-[#222]/60 mt-6 font-light">Book direct for the best available rate — no booking fees, ever.</p>
          <button onClick={() => openBookingModal()} data-testid="cta-book"
            className="mt-9 bg-[#0B192C] text-white px-10 py-4 rounded-full font-medium hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors">
            Check Availability
          </button>
        </Reveal>
      </section>
    </div>
  );
}

// Elegant, touch-friendly reviews carousel.
// One review at a time on mobile, up to three on desktop. Auto-advances every 7s;
// pauses on hover; Prev/Next buttons + dot indicators + swipe.
function ReviewsCarousel({ reviews, idx, setIdx }) {
  const [paused, setPaused] = useState(false);
  const [perView, setPerView] = useState(typeof window !== "undefined" && window.innerWidth >= 1024 ? 3 : (typeof window !== "undefined" && window.innerWidth >= 640 ? 2 : 1));
  useEffect(() => {
    const on = () => setPerView(window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1);
    window.addEventListener("resize", on); return () => window.removeEventListener("resize", on);
  }, []);
  const total = reviews.length;
  const maxIdx = Math.max(0, total - perView);
  useEffect(() => { if (idx > maxIdx) setIdx(0); }, [maxIdx, idx, setIdx]);
  useEffect(() => {
    if (paused || total <= perView) return;
    const t = setInterval(() => setIdx((i) => (i + 1 > maxIdx ? 0 : i + 1)), 7000);
    return () => clearInterval(t);
  }, [paused, total, perView, maxIdx, setIdx]);
  const prev = () => setIdx((i) => (i - 1 < 0 ? maxIdx : i - 1));
  const next = () => setIdx((i) => (i + 1 > maxIdx ? 0 : i + 1));

  // Simple touch swipe support
  const [touch, setTouch] = useState(null);
  const onTouchStart = (e) => setTouch(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touch == null) return;
    const dx = e.changedTouches[0].clientX - touch;
    if (Math.abs(dx) > 40) (dx > 0 ? prev : next)();
    setTouch(null);
  };

  const cardW = 100 / perView;

  return (
    <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="reviews-carousel">
      <div className="overflow-hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <motion.div
          className="flex"
          animate={{ x: `-${idx * cardW}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {reviews.map((r) => (
            <div key={r.id} className="shrink-0 px-3" style={{ width: `${cardW}%` }} data-testid={`review-${r.id}`}>
              <article className="h-full bg-white/5 border border-white/10 rounded-2xl p-6 md:p-7 backdrop-blur-sm flex flex-col">
                <div className="flex gap-1 mb-4">
                  {[...Array(Math.max(1, Math.min(5, r.rating || 5)))].map((_, i) =>
                    <Star key={i} size={15} className="text-[#D4AF37]" fill="#D4AF37" />
                  )}
                </div>
                <p className="text-white/85 font-light italic leading-relaxed font-serif text-lg md:text-xl flex-1">"{r.text}"</p>
                <div className="mt-5 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-white font-medium">{r.guest_name}</p>
                    {r.review_date && <p className="text-white/50 text-xs">{r.review_date}</p>}
                  </div>
                  <a href={r.source_url || "https://www.google.com/maps/search/?api=1&query=Byer+Fountain+Motor+Inn+Holbrook"}
                    target="_blank" rel="noreferrer" className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37]/80 hover:text-[#D4AF37]">
                    {r.source || "Google"} ↗
                  </a>
                </div>
              </article>
            </div>
          ))}
        </motion.div>
      </div>

      {total > perView && (
        <>
          <button onClick={prev} data-testid="reviews-prev"
            className="absolute left-0 md:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-[#0B192C] text-white grid place-items-center transition-colors backdrop-blur">
            <ChevronLeft size={20} />
          </button>
          <button onClick={next} data-testid="reviews-next"
            className="absolute right-0 md:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-[#0B192C] text-white grid place-items-center transition-colors backdrop-blur">
            <ChevronRight size={20} />
          </button>
          <div className="flex justify-center gap-2 mt-8" data-testid="reviews-dots">
            {[...Array(maxIdx + 1)].map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "bg-[#D4AF37] w-8" : "bg-white/25 w-3 hover:bg-white/50"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
